/**
 * Workspace Image — sélecteur de tâche + formulaire unifié + estimation +
 * soumission locale. La génération hébergée (machine connectée) arrive en
 * Phase 3 avec le workspace Compte.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { executionPicker } from "../../components/ExecutionPicker.js";
import { confirmDialog } from "../../components/Modal.js";
import { createReadinessPanel } from "../../components/ReadinessPanel.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import { formatGb } from "../../components/format.js";
import { icon } from "../../components/icons.js";
import { componentsForRequest } from "../../services/components.js";
import { runGeneration, serverGenerates } from "../../services/execution.js";
import { routeToOwnMachine } from "../../services/hosted-jobs.js";
import { prepareOnMachine } from "../../services/model-actions.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import { TaskForm } from "../../tasks/TaskForm.js";
import { imageTasks } from "../../tasks/imageTasks.js";
import type { ModelInfo, WorkerCapabilities } from "../../types/index.js";
import { formatTime } from "../../utils/format.js";
import { createFirstImageCard } from "./FirstImageCard.js";
import { createGettingStarted } from "./GettingStarted.js";
import { createModelPicker } from "./ModelPicker.js";
import { OptimizationsPanel } from "./OptimizationsPanel.js";

export const imageWorkspace: WorkspaceDef = {
	id: "image",
	title: "Image",
	icon: "image",
	build(container, ctx) {
		container.appendChild(h("h2", { class: "workspace-title" }, "Créer une image"));

		const optimisations = new OptimizationsPanel({
			onChange: () => {
				requestEstimation();
				showAccessories();
			},
		});
		// What the options chosen in the form fetch beyond the model itself
		// (a ControlNet, its detector, IP-Adapter, the prompt enhancer, the fast
		// preview decoder): until now each one downloaded inside the first
		// image that used it.
		const accessories = createReadinessPanel();

		const estimationBar = h(
			"div",
			{ class: "estimation-bar" },
			h("span", null, "Estimation en attente…")
		);
		const preflightLine = h("small", { class: "help-text", "aria-live": "polite" });

		const form = new TaskForm(imageTasks, {
			onChange: () => {
				requestEstimation();
				showAccessories();
			},
			onSubmit: () => void submit(),
		});

		// L'estimation et « Générer sur » décrivent ce qui va se passer quand on
		// appuiera : ils se lisent au-dessus du bouton. Ils étaient dessous,
		// donc sur un téléphone la machine choisie était hors écran au moment
		// même où l'on décidait de lancer.
		// Every fold above the button, as on the other pages: « Réglages
		// mémoire » came after Generate, the only thing below it.
		const memory = accordion({
			title: "Réglages mémoire",
			children: [optimisations.element],
			open: false,
		}).element;
		form.addBeforeSubmit(
			memory,
			accessories.element,
			executionPicker(),
			estimationBar,
			preflightLine
		);
		// The model, where the form is: it was only choosable in the Library.
		const picker = createModelPicker();
		form.addAtTop(picker.element);
		// Coming back to this page (from the Library, after a download) asks again.
		new MutationObserver(() => {
			if (!container.hidden) void picker.refresh();
		}).observe(container, { attributes: true, attributeFilter: ["hidden"] });
		// A newcomer's first image, one button away; gone once a model is ready.
		const firstImage = createFirstImageCard(
			async (_model, prompt) => {
				// A plain image, whatever mode the form had kept from earlier.
				form.setTask("txt2img");
				form.setValue("prompt", prompt);
				await submit();
			},
			() => placeForm()
		);
		// Before a computer is connected: the three steps, in place of a form
		// that could not do anything yet.
		const gettingStarted = createGettingStarted(() => placeForm());
		container.appendChild(gettingStarted.element);
		container.appendChild(firstImage.element);
		container.appendChild(form.element);

		/**
		 * One thing to do at a time. The first-image card and the full form
		 * competed on the same screen (audit 03/10): while the card stands, the
		 * form waits behind « Je préfère choisir moi-même », which dismisses it.
		 */
		function placeForm(): void {
			const waiting = gettingStarted.isShown() || !firstImage.element.hidden;
			if (form.element.hidden && !waiting) void picker.refresh();
			form.element.hidden = waiting;
			memory.hidden = waiting;
		}
		placeForm();

		/** The accessory families (app/core/family_models.py) the form asks for. */
		function showAccessories(): void {
			const arch = store.get().selectedModel?.architecture ?? "";
			// Read from the request the form would send, so it is what the machine
			// will be asked for, not a second reading of the fields.
			const request = form.buildRequest();
			const families: string[] = [];
			if (form.getTask() === "controlnet") {
				const type = request.controlnet_type ?? "canny";
				families.push(`controlnet:${arch}:${type}`);
				// Canny is computed by OpenCV; the others by a detector's weights.
				if (type !== "canny") families.push(`controlnet_detector:${type}`);
			}
			if (request.ip_adapter_image) families.push(`ip_adapter:${arch}`);
			if (request.enhance_prompt) families.push("prompt_enhancer");
			if (optimisations.getOptions().enable_tiny_vae) families.push(`tiny_vae:${arch}`);
			accessories.show(families);
		}

		// --- Estimation (debounce côté Estimator) ---
		let estimateToken = 0;

		function currentParams() {
			return {
				width: (form.getValue("width") as number) ?? 1024,
				height: (form.getValue("height") as number) ?? 1024,
				steps: (form.getValue("steps") as number) ?? 30,
				scheduler: (form.getValue("scheduler") as string) || "euler_a",
			};
		}

		function requestEstimation(): void {
			const target = store.get().executionTarget;
			if (target.kind === "hosted") {
				void estimateOnOwnMachine(target.worker);
				return;
			}
			if (!serverGenerates()) {
				// The hosted site has no GPU to estimate against: /api/estimate-time
				// is a local-only route there (a 404). The figure that means
				// something is the user's own machine's, once it is connected.
				estimationBar.replaceChildren(
					h("span", null, "Estimation disponible une fois votre ordinateur relié (Compte).")
				);
				return;
			}
			const model = store.get().selectedModel;
			const params = currentParams();
			const myToken = ++estimateToken;
			ctx.estimator.onEstimateUpdate((result) => {
				if (myToken !== estimateToken) return;
				if (!result) {
					estimationBar.replaceChildren(h("span", null, "Estimation indisponible"));
					return;
				}
				const seconds = result.estimated_seconds > 0 ? formatTime(result.estimated_seconds) : "—";
				const vram = result.vram_required_gb
					? `~${formatGb(result.vram_required_gb)} de mémoire graphique`
					: "";
				const cls =
					result.vram_status === "insufficient"
						? "est-error"
						: result.vram_status === "tight"
							? "est-warning"
							: "est-value";
				estimationBar.replaceChildren(
					...[
						h(
							"span",
							{ class: "est-time" },
							icon("clock"),
							h("span", { class: "est-value" }, seconds)
						),
						h("span", { class: cls }, vram),
						result.message ? h("span", null, result.message) : null,
					].filter((el): el is HTMLElement => el !== null)
				);
			});
			ctx.estimator.requestEstimate({
				...params,
				optimizationOptions: optimisations.getOptions(),
				architecture: model?.architecture,
				hasControlnet: form.getTask() === "controlnet",
			});
		}

		// --- Estimation sur la machine pairée (site hébergé) ---
		// Asked of the router, which knows the paired machine's own hardware
		// from its heartbeat -- never of the API host, a VM with no GPU. The
		// redesign only knew the local route, so on the public site the bar
		// said nothing where the previous interface named the user's machine.
		let hostedEstimateToken = 0;
		async function estimateOnOwnMachine(worker: WorkerCapabilities): Promise<void> {
			const myToken = ++hostedEstimateToken;
			const model = store.get().selectedModel;
			const params = currentParams();
			const prompt = String(form.getValue("prompt") ?? "").trim();
			try {
				const decision = await APIClient.estimateHostedJob(
					"image",
					{
						// The prompt does not change the estimate; validation wants one.
						prompt: prompt || "estimation",
						width: params.width,
						height: params.height,
						num_inference_steps: params.steps,
						scheduler: params.scheduler,
						...(model ? { model_id: model.id, architecture: model.architecture } : {}),
						// Each LoRA costs time: the estimate counts them.
						loras: componentsForRequest(model?.architecture).loras,
					},
					routeToOwnMachine(worker.worker_id)
				);
				if (myToken !== hostedEstimateToken) return;
				const seconds =
					decision.estimated_seconds > 0 ? formatTime(decision.estimated_seconds) : "—";
				estimationBar.replaceChildren(
					h(
						"span",
						{ class: "est-time" },
						icon("clock"),
						h("span", { class: "est-value" }, seconds)
					),
					h("span", null, `sur ${worker.gpu_name ?? "votre ordinateur"}`),
					h("small", { class: "est-note" }, estimateCovers(worker, model, decision.queue_seconds))
				);
			} catch {
				if (myToken !== hostedEstimateToken) return;
				estimationBar.replaceChildren(h("span", null, "Estimation indisponible"));
			}
		}

		// One preparation per model, machine and size: the preflight runs on
		// every change of the form, the load must not.
		const prepared = new Set<string>();
		function prepareOnce(modelId: string, width: number, height: number): void {
			const target = store.get().executionTarget;
			if (serverGenerates() || target.kind !== "hosted") return;
			const key = `${target.worker.worker_id}|${modelId}|${width}x${height}`;
			if (prepared.has(key)) return;
			prepared.add(key);
			void prepareOnMachine(modelId, width, height);
		}

		// --- Preflight discret quand un modèle est choisi ---
		function requestPreflight(): void {
			const model = store.get().selectedModel;
			if (!model) {
				preflightLine.textContent =
					"Choisissez un modèle dans la Bibliothèque pour préparer la génération.";
				return;
			}
			const params = currentParams();
			ctx.estimator.onPreflightUpdate((result) => {
				if (!result) {
					preflightLine.textContent = "";
					return;
				}
				// What stands in the way first, in the Library's own words: a model
				// not yet on the machine, a licence to accept, an account to link.
				// The memory verdict is the last of those steps, not a separate story.
				const next = result.readiness?.next;
				// Whole on the machine: load it now, while the prompt is written.
				if (result.readiness?.ready) prepareOnce(model.id, params.width, params.height);
				if (next && next.key !== "memory") {
					preflightLine.textContent = `${next.title}${next.detail ? ` — ${next.detail}` : ""}`;
					return;
				}
				const warning = result.warnings[0] ?? result.recommendations[0] ?? "";
				// The plan's own words (« float16 en un passage (~9.5 / 14.2GB
				// libre) ») are for whoever tunes the planner: kept as the tooltip,
				// not as the sentence a newcomer reads (UX audit 03/10).
				preflightLine.title = result.plan_reason ?? "";
				preflightLine.textContent = result.plan_fits
					? `Prêt : votre ordinateur a assez de mémoire pour ce modèle.${warning ? ` ${warning}` : ""}`
					: `Attention : ${result.plan_reason}${warning ? ` — ${warning}` : ""}`;
			});
			ctx.estimator.requestPreflight({
				modelId: model.id,
				...params,
				batchSize: (form.getValue("batch_size") as number) ?? 1,
				optimizationOptions: optimisations.getOptions(),
				architecture: model.architecture,
			});
		}

		store.subscribe(
			(s) => s.selectedModel,
			(model) => {
				form.setModel(model);
				requestEstimation();
				requestPreflight();
				showAccessories();
			}
		);
		// Switching between this computer and a paired machine changes whose
		// hardware the estimate is about.
		store.subscribe(
			(s) => s.executionTarget,
			() => requestEstimation()
		);
		form.setModel(store.get().selectedModel);
		// The first estimate: TaskForm no longer fires onChange from inside its
		// own constructor, where `form` was not yet assigned.
		requestEstimation();
		requestPreflight();
		void firstImage.refresh();

		// --- Soumission ---
		async function submit(): Promise<void> {
			const model = store.get().selectedModel;
			const request = form.buildRequest();
			if (!request.prompt.trim()) {
				toast("Décrivez d'abord l'image souhaitée dans le prompt.", "warning");
				return;
			}
			if (!model) {
				toast("Choisissez un modèle dans la Bibliothèque.", "warning");
				return;
			}
			const needsImage =
				form.getTask() === "img2img" || form.getTask() === "edit" || form.getTask() === "inpaint";
			if (needsImage && !request.init_image) {
				toast("Ajoutez une image source pour ce mode.", "warning");
				return;
			}
			if (form.getTask() === "inpaint" && !request.mask_image) {
				toast("Ajoutez un masque (blanc = zone à repeindre).", "warning");
				return;
			}
			if (form.getTask() === "controlnet" && !request.control_image) {
				toast("Ajoutez l'image de référence ControlNet.", "warning");
				return;
			}
			if (
				(model.license_id && !model.license_verified) ||
				(model.gated && !alreadyOnTheMachineThatWillRunIt(model))
			) {
				const label = model.license_id ?? "du modèle";
				// "unknown" / "unknown-local" are this app's own markers, not a
				// licence anyone publishes under: say what they mean.
				const unknown = !model.license_id || model.license_id.startsWith("unknown");
				const ok = await confirmDialog(
					"Licence du modèle",
					unknown
						? "La licence de ce modèle n'est pas connue : elle dépend de son auteur. En l'utilisant, vous acceptez ses conditions. Continuer ?"
						: `Ce modèle est publié sous licence ${label}. En l'utilisant, vous acceptez ses conditions. Continuer ?`
				);
				if (!ok) return;
				request.license_acknowledged = true;
			}

			request.model_id = model.id;
			// La famille du modèle voyage avec la requête : sans elle le
			// routeur ne peut pas écarter une machine qui ne sait pas la
			// faire, et l'estimation ignorait le modèle — elle annonçait
			// 2,667 s pour DreamShaper comme pour FLUX.
			request.architecture = model.architecture;
			// LoRA et VAE voyagent avec l'image : la machine qui la fait les
			// applique pour elle seule (aucun = aucun).
			const components = componentsForRequest(model.architecture);
			request.loras = components.loras;
			request.vae = components.vae;
			request.embeddings = components.embeddings;

			// Chaque machine applique ses propres réglages mémoire : le payload
			// hébergé n'embarque pas optimization_options.
			const { optimization_options: _localOnly, ...hostedPayload } = request;
			try {
				await runGeneration({
					kind: "image",
					payload: hostedPayload,
					hostedFailureMessage: "La génération sur votre ordinateur a échoué.",
					runLocal: async () => {
						const response = await APIClient.generateImage({
							...request,
							optimization_options: optimisations.getOptions(),
						});
						if (!response.success) {
							toast(response.error ?? "Échec de la génération.", "error");
							return;
						}
						const url = response.image_url ?? response.images_urls?.[0] ?? "";
						store.setState({
							currentResult: {
								kind: "image",
								url,
								filename: response.filename ?? "image.png",
								seed: response.seed,
								generationTime: response.generation_time ?? undefined,
								modelId: response.model_id,
								images: response.images_urls,
								warning: response.warning,
								enhancedPrompt: response.enhanced_prompt,
							},
							historyVersion: store.get().historyVersion + 1,
						});
						if (response.escalations?.length) {
							toast(
								`Mémoire ajustée : ${response.escalations.map((e) => e.label).join(", ")}`,
								"warning"
							);
						}
					},
				});
			} catch (error) {
				toast(error instanceof Error ? error.message : "Échec de la génération.", "error");
			}
		}

		// Ctrl+Entrée = générer
		document.addEventListener("keydown", (event) => {
			if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !store.get().run) {
				void submit();
			}
		});
	},
};

/**
 * What the estimate covers, said next to it.
 *
 * The router's number is the generation alone, and it is right: measured on
 * 2026-09-27, SD1.5 at 512x512 in 8 steps took 2.1-3.1 s once the model was
 * resident, against 2.7 s promised. The *first* generation with a model took
 * 50.2 s — forty-eight of them loading weights the estimate says nothing
 * about — so the page promised "3 s" for the one run a newcomer ever sees,
 * and a machine that looked stuck was simply loading.
 *
 * No invented number: one measurement of one model is not a cost model, and
 * replacing a wrong figure with a guessed one is the same mistake. What the
 * page can say truthfully is what the figure is, and what is missing from it.
 */
/**
 * Ce modèle est-il déjà sur la machine qui fera l'image ?
 *
 * `ModelInfo.is_downloaded` décrit le disque de l'hôte de l'API. Sur le site
 * public, cet hôte est une VM sans aucun modèle : le drapeau y vaut `false`
 * pour tout, donc tout modèle *gated* ouvrait une fenêtre de licence avant
 * **chaque** génération, y compris quand la machine de l'utilisateur avait
 * déjà les 24 Go de FLUX sur son disque. Et tant que cette fenêtre attend,
 * rien n'est envoyé et le bouton n'a pas encore dit qu'il travaillait : le
 * propriétaire a cliqué deux fois en croyant le bouton mort (2026-09-28).
 *
 * La machine qui exécutera le job dit elle-même ce qu'elle détient, dans son
 * battement de cœur — la même source que `estimateCovers` ci-dessous.
 */
function alreadyOnTheMachineThatWillRunIt(model: ModelInfo): boolean {
	const target = store.get().executionTarget;
	if (target.kind !== "hosted") return Boolean(model.is_downloaded);
	const machine = target.worker;
	// Un repo auquel il manque un fichier n'est pas détenu, quel que soit son
	// poids : le cache FLUX du propriétaire portait 33,7 Go de poids sans son
	// planificateur ni ses tokenizers, et la question de licence était sautée
	// pour un modèle que la machine allait de toute façon retélécharger.
	if (machine.incomplete_models?.[model.id]) return false;
	return Boolean(
		machine.cached_models_gb?.[model.id] ||
			machine.hub_models?.some((entry) => entry.id === model.id) ||
			machine.local_models?.some((entry) => entry.id === model.id)
	);
}

function estimateCovers(
	worker: WorkerCapabilities,
	model: { id: string; download_size_gb?: number } | null,
	queueSeconds = 0
): string {
	// L'attente est comprise dans le chiffre affiché : sans le dire, une
	// machine occupée passerait pour un modèle lent.
	const covers =
		queueSeconds > 0
			? `Dont ${formatTime(queueSeconds)} d'attente : votre ordinateur finit d'abord ce qu'il a en cours. Le premier lancement d'un modèle y ajoute son chargement.`
			: "Temps de génération seule ; le premier lancement d'un modèle y ajoute son chargement.";
	if (!model) return covers;
	// Ce qui manque à un repo à moitié là se compte en fichiers, pas en
	// gigaoctets : on nomme ce qui manque plutôt que d'inventer une taille.
	const missing = worker.incomplete_models?.[model.id];
	if (missing?.length) {
		return `${covers} Ce modèle est en partie sur votre ordinateur : il lui manque ${missing.slice(0, 3).join(", ")}, à télécharger d'abord.`;
	}
	// The heartbeat says what this machine already holds.
	if (worker.cached_models_gb?.[model.id]) return covers;
	const size = model.download_size_gb;
	return size
		? `${covers} Celui-ci n'est pas encore sur votre ordinateur : ${formatGb(size)} à télécharger d'abord.`
		: `${covers} Celui-ci n'est pas encore sur votre ordinateur : il sera téléchargé d'abord.`;
}
